import {
  React,
  useState,
  useEffect,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Box,
  CustomButton,
} from '../../utils/globalImports';
import { useGetClientsByCustomerIdQuery } from '../../api/clientApiSlice';
import { useTranslation } from 'react-i18next';
import CustomSearchableDropdown from '../dropdown/CustomSearchableDropdown';
import { Client } from '../../pages/client/types';

interface CustomClientSelectDialogProps {
  open: boolean;
  handleClose: () => void;
  handleConfirm: (clientId: string) => void;
  customerId: string;
  dialogTitle?: string;
  label?: string;
  confirmColorPalette?: 'primary' | 'secondary' | 'error' | 'warning' | 'info';
  cancelColorPalette?: 'primary' | 'secondary' | 'error' | 'warning' | 'info';
}

const CustomClientSelectDialog: React.FC<CustomClientSelectDialogProps> = ({
  open,
  handleClose,
  handleConfirm,
  customerId,
  dialogTitle,
  confirmColorPalette = 'primary',
  cancelColorPalette = 'primary',
  label,
}) => {
  const { t } = useTranslation();

  const {
    data: clients,
    isLoading: isLoadingClients,
    isError,
  } = useGetClientsByCustomerIdQuery(customerId || '', {
    skip: !customerId,
  });

  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  useEffect(() => {
    if (clients && clients.length > 0) {
      setSelectedClient(clients[0]);
      return;
    }

    setSelectedClient(null);
  }, [clients]);

  const handleConfirmClick = () => {
    if (selectedClient?.client_id) {
      handleConfirm(selectedClient.client_id);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>{dialogTitle || t('document.selectClientDialogboxTitle')}</DialogTitle>
      <DialogContent>
        {isLoadingClients ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ p: 2 }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box>{t('client.errorFetchingClients')}</Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <CustomSearchableDropdown
              id="client-select-dropdown"
              label={label || t('client.actions.client')}
              placeholder={t('client.actions.client')}
              options={clients || []}
              value={selectedClient}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.client_id === value.client_id}
              onChange={setSelectedClient}
              sx={{ width: '100%' }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton onClick={handleClose} variant="outlined" text={t('client.cancel')} color={cancelColorPalette} />
        <CustomButton
          onClick={handleConfirmClick}
          disabled={!selectedClient?.client_id || isLoadingClients}
          variant="contained"
          text={t('client.confirm')}
          color={confirmColorPalette}
        />
      </DialogActions>
    </Dialog>
  );
};

export default CustomClientSelectDialog;
